import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { SafeMarkdown, type SafeMarkdownProps } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import { OrdinalIcon } from "@/components/OrdinalIcon";
import { sanitizeLLMOutput } from "@/lib/sanitizeLlmOutput";
import "@github/relative-time-element";

type ChatAssistantBubbleProps = {
  content: string;
  createdAt?: string;
  compact?: boolean;
  sanitize?: boolean;
  header?: ReactNode;
  afterContent?: ReactNode;
  footer?: ReactNode;
  markdownComponents?: SafeMarkdownProps["components"];
  urlTransform?: SafeMarkdownProps["urlTransform"];
};

export function ChatAssistantBubble({
  content,
  createdAt,
  compact = false,
  sanitize = true,
  header,
  afterContent,
  footer,
  markdownComponents,
  urlTransform,
}: ChatAssistantBubbleProps) {
  const displayContent = sanitize ? sanitizeLLMOutput(content) : content;
  const proseClass = compact
    ? "grid gap-2 prose prose-sm dark:prose-invert prose-headings:m-0 prose-ol:m-0 prose-ul:m-0 prose-li:m-0 prose-p:m-0 max-w-none mb-4"
    : "grid gap-2 prose dark:prose-invert prose-headings:m-0 prose-ol:m-0 prose-ul:m-0 prose-li:m-0 prose-p:m-0 max-w-none mb-4";

  const avatarClass = compact
    ? "h-8 w-8 flex items-center bg-gray-200 dark:bg-gray-800"
    : "h-8 w-8 lg:h-14 lg:w-14 flex items-center bg-gray-200 dark:bg-gray-800";

  const defaultFooter = (
    <div className="flex items-center gap-2">
      <Avatar className={avatarClass}>
        <OrdinalIcon fill="black" />
      </Avatar>
      {createdAt ? (
        <relative-time datetime={createdAt} className="text-sm text-muted-foreground" />
      ) : null}
    </div>
  );

  // Plain card surface, full width. The user bubble is the tinted, inset one,
  // so a question and its answer are separable without reading the avatars.
  return (
    <Card className="w-full p-4">
      {header}
      <div className={proseClass}>
        <SafeMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
          urlTransform={urlTransform}
        >
          {displayContent || "…"}
        </SafeMarkdown>
      </div>
      {afterContent}
      {footer ?? defaultFooter}
    </Card>
  );
}
